/**
 * THE shared admin gate — fail-closed. ONE implementation, wrapping the whole
 * admin route subtree. No admin page re-implements this (that per-page
 * duplication is exactly the bug class rebuild-lessons.md #2 calls out:
 * grade.html failed OPEN on its own admin check).
 *
 * Layers of defence, all FAIL CLOSED:
 *   1. The shell's ProtectedRoute already guarantees the user is authenticated
 *      (MSAL session present) before any admin route renders — see
 *      src/routing/ProtectedRoute.tsx. So this guard only decides admin-vs-not.
 *   2. We call the server-side gate GET /api/admin/me (the same `checkAdmin()`
 *      the real authz endpoints use). The decision is made on the SERVER, never
 *      from a client-readable role claim.
 *   3. ANY non-success outcome denies:
 *        - query pending  → render a neutral "checking…" state, NOT children
 *        - query error    → render Denied (a 401/network/parse failure is NOT
 *                           an admin; `retry: false` makes the answer final)
 *        - data.admin !== true (explicit) → render Denied
 *      Only an explicit `{ admin: true }` renders the admin chrome.
 *
 * There is no path where an error or missing data falls through to children.
 */
import type { ReactNode } from 'react';
import { useAdminMe } from '../data-access/useAdminMe';
import { AdminDenied } from './AdminDenied';
import { AdminChecking } from './AdminChecking';

export function AdminGuard({ children }: { children: ReactNode }) {
  const { data, isPending, isError } = useAdminMe();

  // Pending: never render children while the answer is unknown.
  if (isPending) {
    return <AdminChecking />;
  }

  // Fail closed: any error (401, network, parse) is treated as "not an admin".
  if (isError) {
    return <AdminDenied reason="error" />;
  }

  // Only an explicit { admin: true } passes. Anything else denies.
  if (data?.admin !== true) {
    return <AdminDenied reason="not-admin" />;
  }

  return <>{children}</>;
}
